import React, { useEffect, useState } from "react";
import {
    Box, Paper, Typography, TextField, Button, CircularProgress, useMediaQuery, Divider
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../contexts/SnackbarContext";
import api from "../../services/api";
import { getApiErrorMessage } from "../../services/apiUtils";

export default function RedefinirSenha() {
    const responsiveGrid = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const { token } = useParams(); // rota: /redefinir/senha/:token

    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [checking, setChecking] = useState(true);
    const [valid, setValid] = useState(false);

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const passwordValue = password.trim();

    const passwordRules = [
        {
            key: "minLength",
            valid: passwordValue.length >= 6,
            message: "mínimo de 6 caracteres",
        },
        {
            key: "hasLetter",
            valid: /[A-Za-zÀ-ÿ]/.test(passwordValue),
            message: "pelo menos uma letra",
        },
        {
            key: "hasNumber",
            valid: /\d/.test(passwordValue),
            message: "pelo menos um número",
        },
    ];

    const pendingPasswordRules = passwordRules.filter((rule) => !rule.valid);

    const isPasswordValid = pendingPasswordRules.length === 0;

    const passwordHelperText =
        password.length === 0
            ? " "
            : isPasswordValid
                ? "Senha válida."
                : `Ainda falta: ${pendingPasswordRules.map((rule) => rule.message).join(", ")}.`;

    const canSubmit =
        isPasswordValid &&
        confirm.trim() === passwordValue;

    // Verifica se o token não expirou ao abrir a página
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                // O seu controller atual de verificarExpiracao lê o token do BODY,
                // então use POST mesmo que sua rota registrada esteja como GET.
                const { data } = await api.post(`/teacher/password-reset/verify/${token}`);
                if (!mounted) return;
                setValid(Boolean(data?.status));
            } catch (_) {
                if (!mounted) return;
                setValid(false);
            } finally {
                if (mounted) setChecking(false);
            }
        })();
        return () => { mounted = false; };
    }, [token]);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!isPasswordValid) {
            showSnackbar("A senha deve ter no mínimo 6 caracteres, contendo letras e números.", "warning");
            return;
        }

        if (confirm.trim() !== passwordValue) {
            showSnackbar("As senhas não conferem.", "warning");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/teacher/password-reset/${token}`, {
                newPassword: passwordValue,
            });

            if (data?.status === true) {
                showSnackbar("Senha redefinida com sucesso!", "success");
                navigate("/login");
            } else if (data?.error === "token_invalido") {
                showSnackbar("Token inválido. Solicite uma nova redefinição.", "error");
            } else if (data?.error === "token_expirado") {
                showSnackbar("O link expirou. Solicite uma nova redefinição.", "warning");
            } else {
                showSnackbar("Não foi possível redefinir a senha.", "error");
            }
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, "Falha ao redefinir. Tente novamente."), "error");
        } finally {
            setSubmitting(false);
        }
    }

    // Loading enquanto checa validade
    if (checking) {
        return (
            <Box sx={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    // Token inválido/expirado
    if (!valid) {
        return (
            <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Paper elevation={3} sx={{ p: 4, width: responsiveGrid ? '90%' : '30%', borderRadius: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                        Redefinição expirada
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        O link de redefinição não é mais válido. Volte ao login e use{" "}
                        <b>“Esqueci minha senha”</b> para solicitar um novo.
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Button variant="contained" fullWidth onClick={() => navigate("/login")}>
                        Voltar ao login
                    </Button>
                </Paper>
            </Box>
        );
    }

    // Form de redefinição
    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Paper elevation={3} sx={{ p: 4, width: responsiveGrid ? '90%' : '30%', borderRadius: 4 }}>
                <Typography variant="h5" mb={2} align="center" sx={{ fontWeight: "bold" }}>
                    Redefinir senha
                </Typography>
                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Nova senha"
                        type="password"
                        fullWidth
                        required
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={password.length > 0 && !isPasswordValid}
                        helperText={passwordHelperText}
                        FormHelperTextProps={{
                            sx: {
                                color: password.length > 0 && isPasswordValid ? "success.main" : undefined,
                                fontWeight: password.length > 0 ? 600 : 400,
                            },
                        }}
                    />
                    <TextField
                        label="Confirmar nova senha"
                        type="password"
                        fullWidth
                        required
                        margin="normal"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        error={confirm.length > 0 && confirm !== password}
                        helperText={confirm.length > 0 && confirm !== password ? "As senhas não conferem." : " "}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        sx={{
                            mt: 2,
                            position: "relative",
                            "&.Mui-disabled": {
                                backgroundColor: "rgba(160,160,160,0.6)",
                                color: "#fff",
                                cursor: "not-allowed",
                            },
                        }}
                        disabled={submitting || !canSubmit}
                    >
                        {submitting && (
                            <CircularProgress size={20} sx={{ color: "white", position: "absolute", left: 16 }} />
                        )}
                        <span style={{ marginLeft: submitting ? 24 : 0 }}>
                            {submitting ? "Atualizando..." : "Atualizar senha"}
                        </span>
                    </Button>
                </form>
            </Paper>
        </Box>
    );
}